/**
 * Helpers for the per-caliber landing page (`/ammo/:caliberSlug`, #250).
 *
 * The landing page is a real, indexable page (not a redirect): it shows live
 * stats and an embedded in-stock result set for one caliber, with its own
 * canonical at `/ammo/:slug`. These pure helpers build its SEO copy, summarize
 * the search payload, and decide when a bare single-caliber *search* should
 * consolidate onto the landing page to avoid duplicate content.
 */

import { breadcrumbList, itemList } from './seo/jsonLd';
import { caliberToSlug } from './caliber';
import type { SearchResponse, SearchHit } from '../services/searchApi';
import type { SearchQuery } from './searchUrl';

export interface CaliberSummary {
  /** Total in-stock listings for the caliber (from search totalResults). */
  inStockCount: number;
  /** Distinct retailers carrying it (from the retailerName facet). */
  retailerCount: number;
  /** Lowest / highest delivered $/round across the fetched results. */
  lowestCpr: number | null;
  highestCpr: number | null;
}

/** Reduce a caliber search response to the headline stats the page shows. */
export function summarizeCaliberListings(res: SearchResponse | undefined): CaliberSummary {
  const cprs = (res?.results ?? [])
    .map((h) => h.pricePerRound)
    .filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0);
  return {
    inStockCount: res?.totalResults ?? 0,
    retailerCount: res?.facets?.retailerName?.length ?? 0,
    lowestCpr: cprs.length > 0 ? Math.min(...cprs) : null,
    highestCpr: cprs.length > 0 ? Math.max(...cprs) : null,
  };
}

/** Unique page/tab title for the caliber. */
export function buildCaliberLandingTitle(caliber: string): string {
  return `${caliber} Ammo — In-Stock Prices`;
}

/** Meta description — data-driven when live stats are available. */
export function buildCaliberLandingDescription(caliber: string, s: CaliberSummary): string {
  if (s.inStockCount > 0 && s.lowestCpr != null) {
    const retailerBit =
      s.retailerCount > 0
        ? ` from ${s.retailerCount} retailer${s.retailerCount === 1 ? '' : 's'}`
        : '';
    return `Compare ${s.inStockCount} in-stock ${caliber} ammo listings${retailerBit} — from $${s.lowestCpr.toFixed(3)}/round. Ranked by price per round, freshness, and retailer trust.`;
  }
  return `Compare in-stock ${caliber} ammunition by price per round across many retailers on Round Recon.`;
}

/** BreadcrumbList (Home › Ammo › caliber) + an ItemList of the top results. */
export function buildCaliberLandingJsonLd(
  caliber: string,
  canonical: string,
  hits: SearchHit[]
): object[] {
  const out: object[] = [
    breadcrumbList([
      { name: 'Round Recon', url: '/' },
      { name: 'Ammo', url: '/search' },
      { name: caliber, url: canonical },
    ]),
  ];
  if (hits.length > 0) {
    out.push(
      itemList(
        hits.slice(0, 20).map((h, i) => ({
          position: i + 1,
          name: h.title ?? undefined,
          url: h.visitUrl,
        }))
      )
    );
  }
  return out;
}

/**
 * When a search is a *bare* single-caliber facet, the caliber landing page
 * (`/ammo/:slug`) is its canonical home — return that path so the search page
 * consolidates onto the landing page instead of competing as duplicate content
 * (#250). Returns `null` for any richer query (free text, extra filters,
 * multiple/zero calibers, price/round/lot constraints, deep pages). The
 * in-stock / exclude-remanufactured toggles are allowed since the landing page
 * represents the caliber either way.
 */
export function caliberLandingPathForSearch(q: SearchQuery): string | null {
  if (q.caliber.length !== 1) return null;
  if (q.q && q.q.trim().length > 0) return null;
  if (q.page > 1) return null;
  const otherFacets = [
    q.ammoType,
    q.brand,
    q.productLine,
    q.bulletType,
    q.bulletWeightGrains,
    q.caseMaterial,
    q.retailerId,
    q.useCases,
  ];
  if (otherFacets.some((a) => a.length > 0)) return null;
  if (q.minPricePerRound != null || q.maxPricePerRound != null || q.minRoundCount != null) {
    return null;
  }
  if (q.lotNumber || q.stockStatus) return null;
  return `/ammo/${caliberToSlug(q.caliber[0]!)}`;
}
